import { getDocumentContent } from './qirtaas'
import { getCurrentUser } from './auth'
import { matchConcept, normalizeConcept } from './conceptGlossary'

type Doc = { id:string; title:string; updated_at:string }
type JsonNode = { type?:string; text?:string; attrs?:Record<string,unknown>; content?:JsonNode[] }
export type KnowledgeHit = { id:string; title:string; snippet:string; score:number }

function searchableText(node:JsonNode|undefined):string {
  if(!node)return ''
  const parts:string[]=[]
  if(typeof node.text==='string')parts.push(node.text)
  if(node.type==='quranVerse'||node.type==='hadithNode'){
    const a=node.attrs||{}
    for(const key of ['text','translation','translationEn','surahNameArabic','surahNameEnglish','collectionNameArabic','collectionNameEnglish','researchSourceTranslation','researchContextTranslation','researchContextLabel','researchMeta']){
      const value=a[key]
      if(typeof value==='string')parts.push(value)
    }
  }
  for(const child of node.content||[])parts.push(searchableText(child))
  return parts.join(' ').replace(/\s+/g,' ').trim()
}

function snippet(text:string,query:string):string {
  const lower=text.toLowerCase(),needle=query.toLowerCase(),at=lower.indexOf(needle)
  const start=Math.max(0,(at<0?0:at)-75),end=Math.min(text.length,(at<0?0:at)+needle.length+145)
  return `${start?'…':''}${text.slice(start,end).trim()}${end<text.length?'…':''}`
}

// Searching "All studies" reads every note in scope. Reads are limited to READ_CONCURRENCY at
// a time (previously all ~620 were requested at once), superseded searches are cancelled via
// AbortSignal, and extracted text is cached per document version (id + updated_at) so repeated
// searches do not re-read unchanged notes. Notes that are open/edited (freshIds) bypass the cache.
const READ_CONCURRENCY=6
const TEXT_TTL_MS=60*60_000
const textCache=new Map<string,{updatedAt:string;text:string;at:number}>()
const RESULT_TTL_MS=20*60_000
const resultCache=new Map<string,{at:number;signature:string;rows:StudyContext[]}>()

// IndexedDB stores extracted text locally, partitioned by the authenticated Dawwin user.
// Only documents returned by the current authorised document list are ever read.
// Browsers without IndexedDB continue with the in-memory cache.
const INDEX_DB='dawwin-explore-index-v1'
const INDEX_STORE='documents'
type IndexedText={key:string;version:string;text:string;at:number}
let indexDatabase:Promise<IDBDatabase|null>|null=null
function openIndex():Promise<IDBDatabase|null>{
  if(typeof indexedDB==='undefined')return Promise.resolve(null)
  if(indexDatabase)return indexDatabase
  indexDatabase=new Promise(resolve=>{
    try{
      const request=indexedDB.open(INDEX_DB,1)
      request.onupgradeneeded=()=>{if(!request.result.objectStoreNames.contains(INDEX_STORE))request.result.createObjectStore(INDEX_STORE,{keyPath:'key'})}
      request.onsuccess=()=>resolve(request.result)
      request.onerror=()=>resolve(null)
      request.onblocked=()=>resolve(null)
    }catch{resolve(null)}
  })
  return indexDatabase
}
async function indexGet(key:string):Promise<IndexedText|null>{
  const db=await openIndex()
  if(!db)return null
  return new Promise(resolve=>{
    try{
      const req=db.transaction(INDEX_STORE,'readonly').objectStore(INDEX_STORE).get(key)
      req.onsuccess=()=>resolve((req.result as IndexedText|undefined)||null)
      req.onerror=()=>resolve(null)
    }catch{resolve(null)}
  })
}
async function indexGetMany(keys:string[]):Promise<Map<string,IndexedText>>{
  const result=new Map<string,IndexedText>()
  const db=await openIndex()
  if(!db||!keys.length)return result
  return new Promise(resolve=>{
    try{
      const tx=db.transaction(INDEX_STORE,'readonly')
      const store=tx.objectStore(INDEX_STORE)
      for(const key of keys){
        const req=store.get(key)
        req.onsuccess=()=>{if(req.result)result.set(key,req.result as IndexedText)}
      }
      tx.oncomplete=()=>resolve(result)
      tx.onerror=()=>resolve(result)
      tx.onabort=()=>resolve(result)
    }catch{resolve(result)}
  })
}
async function indexPut(value:IndexedText):Promise<void>{
  const db=await openIndex()
  if(!db)return
  return new Promise(resolve=>{
    try{
      const req=db.transaction(INDEX_STORE,'readwrite').objectStore(INDEX_STORE).put(value)
      req.onsuccess=()=>resolve()
      req.onerror=()=>resolve()
    }catch{resolve()}
  })
}
async function userIndexScope():Promise<string|null>{
  try{return (await getCurrentUser()).id}catch{return null}
}
export type SearchOptions={signal?:AbortSignal;freshIds?:string[];onProgress?:(progress:SearchProgress)=>void;indexScope?:string|null}
export type SearchProgress={done:number;total:number;failed:number}

async function noteText(doc:Doc,signal:AbortSignal|undefined,fresh:boolean,scope?:string|null,preloaded?:Map<string,IndexedText>):Promise<string|null>{
  const cacheKey=(scope||'volatile')+':'+doc.id
  const cached=textCache.get(cacheKey)
  if(!fresh&&cached&&cached.updatedAt===(doc.updated_at||'')&&Date.now()-cached.at<TEXT_TTL_MS)return cached.text
  const key=scope?scope+':'+doc.id:null
  if(!fresh&&key){
    const indexed=preloaded?preloaded.get(key):await indexGet(key)
    if(signal?.aborted)throw new DOMException('Search superseded','AbortError')
    if(indexed&&indexed.version===(doc.updated_at||'')){
      textCache.set(cacheKey,{updatedAt:indexed.version,text:indexed.text,at:Date.now()})
      return indexed.text
    }
  }
  try{
    const result=await getDocumentContent(doc.id,{signal})
    const text=searchableText(result.content as JsonNode|undefined)
    if(text){
      textCache.set(cacheKey,{updatedAt:doc.updated_at||'',text,at:Date.now()})
      if(key)await indexPut({key,version:doc.updated_at||'',text,at:Date.now()})
    }
    return text
  }catch(error){
    if(signal?.aborted)throw error
    return null
  }
}

async function readAll<T>(documents:Doc[],opts:SearchOptions,score:(doc:Doc,text:string)=>T|null):Promise<T[]>{
  const fresh=new Set(opts.freshIds||[]),results:(T|null)[]=new Array(documents.length).fill(null)
  const progress:SearchProgress={done:0,total:documents.length,failed:0}
  opts.onProgress?.({...progress})
  const scope=opts.indexScope===undefined?await userIndexScope():opts.indexScope
  const pending=scope?documents.filter(doc=>{
    const c=textCache.get(scope+':'+doc.id)
    return fresh.has(doc.id)||!c||c.updatedAt!==(doc.updated_at||'')||Date.now()-c.at>=TEXT_TTL_MS
  }):[]
  const preloaded=scope?await indexGetMany(pending.filter(doc=>!fresh.has(doc.id)).map(doc=>scope+':'+doc.id)):undefined
  if(opts.signal?.aborted)throw new DOMException('Search superseded','AbortError')
  let next=0
  const worker=async()=>{
    while(next<documents.length){
      if(opts.signal?.aborted)throw new DOMException('Search superseded','AbortError')
      const index=next++,doc=documents[index]
      const text=await noteText(doc,opts.signal,fresh.has(doc.id),scope,preloaded)
      if(text===null)progress.failed++
      else if(text)results[index]=score(doc,text)
      progress.done++
      if(progress.done===progress.total||progress.done%25===0)opts.onProgress?.({...progress})
    }
  }
  await Promise.all(Array.from({length:Math.min(READ_CONCURRENCY,documents.length)},worker))
  return results.filter((r):r is T=>r!==null)
}

export async function searchKnowledge(documents:Doc[],query:string,opts:SearchOptions={}):Promise<KnowledgeHit[]> {
  const q=query.trim().toLowerCase()
  if(q.length<2)return []
  const rows=await readAll(documents,opts,(doc,body)=>{
    const title=(doc.title||'Untitled').toLowerCase()
    const bodyLower=body.toLowerCase()
    if(!title.includes(q)&&!bodyLower.includes(q))return null
    const titleScore=title===q?100:title.startsWith(q)?60:title.includes(q)?35:0
    const occurrences=bodyLower.split(q).length-1
    return {id:doc.id,title:doc.title||'Untitled',snippet:snippet(body,q),score:titleScore+Math.min(occurrences,20)}
  })
  return rows.sort((a,b)=>b.score-a.score||a.title.localeCompare(b.title))
}

// Curated cross-script equivalences only: never transliterate arbitrary words by guesswork.
const SEARCH_EQUIVALENTS:Record<string,string[]>={
 nafs:['nafs','al-nafs','an-nafs','النفس','نفس','النَّفْس','نَفْس'],
 qalb:['qalb','al-qalb','القلب','قلب','قَلْب'],
 ruh:['ruh','rooh','al-ruh','الروح','روح','رُوح'],
 tazkiyah:['tazkiyah','tazkiyat','تزكية','التزكية','تزكيه'],
 ammarah:['ammarah','al-ammarah','أمارة','الأمارة','امارة'],
 lawwamah:['lawwamah','al-lawwamah','لوامة','اللوامة'],
 mutmainnah:['mutmainnah','mutmainna','مطمئنة','المطمئنة']
}
export function canonicalSearch(text:string):string {
 return normalizeConcept(text).replace(/[أإآٱ]/g,'ا').replace(/ى/g,'ي').replace(/ـ/g,'')
}
export function searchVariants(question:string):string[] {
 const canonical=canonicalSearch(question)
 const entry=matchConcept(question)
 const id=entry?.id||Object.keys(SEARCH_EQUIVALENTS).find(k=>SEARCH_EQUIVALENTS[k].some(v=>canonicalSearch(v)===canonical))
 if(!id)return [canonical].filter(Boolean)
 return [...new Set([canonical,...(SEARCH_EQUIVALENTS[id]||[]).map(canonicalSearch)].filter(Boolean))]
}
/** Convert an offset in diacritic-insensitive search text back to the original note.
 * Normalisation can remove tashkil, ligatures and punctuation; using its offset
 * directly on the source silently produces unrelated evidence excerpts. */
export function sourceOffsetForCanonical(text:string,canonicalOffset:number):number {
 if(canonicalOffset<=0)return 0
 let low=0,high=text.length
 // Find the character that *begins* at this canonical offset.
 // The prefix must grow past the offset, not merely reach it.
 while(low<high){
   const mid=Math.floor((low+high)/2)
   if(canonicalSearch(text.slice(0,mid+1)).length<=canonicalOffset)low=mid+1
   else high=mid
 }
 return low
}
function matchVariant(text:string,variant:string):number {
 const escaped=variant
 const re=new RegExp('(?<![\\p{L}\\p{N}])'+escaped+'(?![\\p{L}\\p{N}])','gu')
 const match=re.exec(text)
 return match?match.index:-1
}
export type StudyContext={id:string;title:string;text:string;score:number}
const stopWords=new Set(['the','and','for','that','with','this','from','what','when','where','which','into','about','have','does','were','your','their','then','than','also','how','why','who','are','was'])
// "All studies" on any device: the server's search copy of the notes answers at once when it is complete, instead of
// this browser reading every note. Notes open for editing (freshIds) are still read here, as they may be newer than the
// copy. Anything else (a study space, Explore's full listing, an incomplete copy, the server unreachable) reads notes here.
const NOTES_SEARCH='https://api.dawwin.al-ilm.net/notes-search'
type ServerSearch={hits:{id:string;title:string;snippet:string;score:number}[];status:{indexed:number;listed:number;syncing:boolean}}
async function serverStudyContext(documents:Doc[],question:string,limit:number,signal?:AbortSignal):Promise<StudyContext[]|null>{
 if(limit>50)return null
 const words=(question.match(/[\p{L}\p{N}]+/gu)||[]).filter(w=>w.length>=2&&!stopWords.has(w.toLowerCase()))
 if(!words.length)return null
 try{
  const r=await fetch(`${NOTES_SEARCH}?mode=any&limit=50&q=${encodeURIComponent(words.join(' '))}`,{credentials:'include',signal,headers:{Accept:'application/json'}})
  if(!r.ok)return null
  const j=await r.json() as ServerSearch
  const st=j.status
  if(!st||!st.listed||st.indexed<st.listed||documents.length<st.listed*0.9)return null
  const inScope=new Set(documents.map(d=>d.id))
  return j.hits.filter(h=>inScope.has(h.id)).slice(0,limit).map(h=>({id:h.id,title:h.title,text:h.snippet.replace(/[«»]/g,''),score:h.score}))
 }catch{return null}
}
export async function retrieveStudyContext(documents:Doc[],question:string,limit=8,opts:SearchOptions={}):Promise<StudyContext[]>{
 const server=await serverStudyContext(documents,question,limit,opts.signal)
 if(!server)return retrieveFromNotes(documents,question,limit,opts)
 const freshIds=new Set(opts.freshIds||[]),fresh=documents.filter(d=>freshIds.has(d.id))
 if(!fresh.length){opts.onProgress?.({done:documents.length,total:documents.length,failed:0});return server}
 const local=await retrieveFromNotes(fresh,question,limit,opts)
 return[...local,...server.filter(s=>!freshIds.has(s.id))].slice(0,Math.max(1,limit))
}
async function retrieveFromNotes(documents:Doc[],question:string,limit=8,opts:SearchOptions={}):Promise<StudyContext[]>{
 const terms=searchVariants(question).filter(t=>t.length>=2&&!stopWords.has(t))
 if(!terms.length)return[]
 const scope=await userIndexScope()
 const signature=documents.map(doc=>doc.id+':'+(doc.updated_at||'')).join('|')
 const key=(scope||'volatile')+'|'+terms.join(' ')+'|'+limit
 const cached=resultCache.get(key)
 if(!opts.freshIds?.length&&cached&&cached.signature===signature&&Date.now()-cached.at<RESULT_TTL_MS){
   opts.onProgress?.({done:documents.length,total:documents.length,failed:0})
   return cached.rows
 }
 let failedReads=0
 const rows=await readAll(documents,{...opts,indexScope:scope,onProgress:p=>{failedReads=p.failed;opts.onProgress?.(p)}},(doc,body)=>{const lower=canonicalSearch(body),title=canonicalSearch(doc.title||'Untitled');let score=0,best=-1;for(const term of terms){if(matchVariant(title,term)>=0)score+=8;const at=matchVariant(lower,term);if(at>=0){score+=2;best=best<0?at:Math.min(best,at)}}if(!score)return null;const originalAt=best<0?0:sourceOffsetForCanonical(body,best);const start=Math.max(0,originalAt-200),text=body.slice(start,start+2600).trim();return{id:doc.id,title:doc.title||'Untitled',text,score}})
 if(failedReads===documents.length&&documents.length>0)throw new Error('All note content requests failed')
 const sorted=rows.sort((a,b)=>b.score-a.score||a.title.localeCompare(b.title)).slice(0,Math.max(1,limit))
 if(!opts.signal?.aborted&&failedReads===0){
   resultCache.set(key,{at:Date.now(),signature,rows:sorted})
   if(resultCache.size>12)resultCache.delete(resultCache.keys().next().value!)
 }
 return sorted
}
